import { dashboardWidgetsFor, getDashboardWidget } from "@repo/shared";
import {
  DASHBOARD_KINDS,
  type DashboardWidgetKey,
  type DashboardWidgetZone,
} from "@repo/shared/types";
import { useMemo } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { SectionHeader } from "@/components/ui/section-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTabParam } from "@/hooks/use-tab-param";
import {
  useDashboardSettings,
  useUpdateDashboardWidgets,
} from "@/lib/hooks/use-dashboard-settings";
import { useT } from "@/lib/i18n/use-t";
import { CashThresholdEditor } from "./cash-threshold-editor";
import { T } from "./dashboard-settings.i18n";
import { LayoutPreview } from "./layout-preview";
import { WidgetTable } from "./widget-table";

const NO_WIDGETS: DashboardWidgetKey[] = [];

/**
 * Settings → Dashboards (QL-357): the designer switches widgets on and drags
 * their order, per dashboard, for the whole tenant. Every change saves on its
 * own — there is no Save button.
 */
export function DashboardSettings() {
  const t = useT(T);
  const { settings, isLoading, error } = useDashboardSettings();
  const update = useUpdateDashboardWidgets();
  const [dashboard, setDashboard] = useTabParam(DASHBOARD_KINDS, "user");

  const widgets = settings?.[dashboard] ?? NO_WIDGETS;
  // Memoised so the drag hook only resets its order when the saved list changes.
  const shownKeyFigures = useMemo(
    () => widgets.filter((k) => getDashboardWidget(k).zone === "key_figure"),
    [widgets],
  );
  const shownSections = useMemo(
    () => widgets.filter((k) => getDashboardWidget(k).zone === "section"),
    [widgets],
  );
  const available = (zone: DashboardWidgetZone) =>
    dashboardWidgetsFor(dashboard).filter((w) => w.zone === zone);

  const save = (next: DashboardWidgetKey[]) =>
    update.mutateAsync({ dashboard, widgets: next }).catch((err) => {
      toast.error(t.saveFailed);
      throw err;
    });

  // The stored list keeps key figures before sections; each zone orders itself.
  const withZone = (zone: DashboardWidgetZone, shown: DashboardWidgetKey[]) =>
    zone === "key_figure" ? [...shown, ...shownSections] : [...shownKeyFigures, ...shown];

  const toggle = (zone: DashboardWidgetZone) => (key: DashboardWidgetKey, on: boolean) => {
    const current = zone === "key_figure" ? shownKeyFigures : shownSections;
    const next = on ? [...current, key] : current.filter((k) => k !== key);
    save(withZone(zone, next)).catch(() => {});
  };

  const dashboardName = dashboard === "user" ? t.userDashboard : t.performanceDashboard;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.dashboards }]}
        title={t.dashboards}
        description={t.description}
      />

      <div className="space-y-2">
        <Tabs value={dashboard} onValueChange={setDashboard}>
          <TabsList>
            <TabsTrigger value="user">{t.userDashboard}</TabsTrigger>
            <TabsTrigger value="performance">{t.performanceDashboard}</TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-sm text-muted-foreground">
          {dashboard === "user" ? t.userVisibility : t.performanceVisibility}
        </p>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="info">{t.complianceOnly}</Badge>
          {dashboard === "user" ? t.userComplianceNote : t.performanceComplianceNote}
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full rounded-lg" />
      ) : error || !settings ? (
        <EmptyState>{t.unableToLoad}</EmptyState>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_368px]">
          <div className="space-y-6">
            <div className="space-y-3">
              <SectionHeader title={t.keyFigures} description={t.keyFiguresDescription} />
              <WidgetTable
                key={`${dashboard}-key-figures`}
                shown={shownKeyFigures}
                available={available("key_figure")}
                onReorder={(shown) => save(withZone("key_figure", shown))}
                onToggle={toggle("key_figure")}
              />
            </div>
            <div className="space-y-3">
              <SectionHeader title={t.sections} description={t.sectionsDescription} />
              <WidgetTable
                key={`${dashboard}-sections`}
                shown={shownSections}
                available={available("section")}
                onReorder={(shown) => save(withZone("section", shown))}
                onToggle={toggle("section")}
                renderBelow={(key) =>
                  key === "cash_to_invest" ? (
                    <CashThresholdEditor
                      threshold={settings.cashThreshold}
                      currency={settings.currency}
                    />
                  ) : null
                }
              />
              <div className="space-y-1 text-sm text-muted-foreground">
                {dashboard === "performance" && <p>{t.userOnlyNote}</p>}
                <p>{t.alwaysShown}</p>
              </div>
            </div>
          </div>
          <div className="lg:sticky lg:top-6">
            <LayoutPreview widgets={widgets} dashboardName={dashboardName} />
          </div>
        </div>
      )}
    </div>
  );
}
